import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  IconButton,
  Paper,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Table,
  Tabs,
  Tab,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserTie } from "@fortawesome/free-solid-svg-icons";

import { getRuta } from "../../../helpers/Helpers";
import { tienePermiso } from "../../../helpers/permisos";
import { getToken } from "../../../api/Account";
import { DataContext } from "../../../context/DataContext";
import { deleteEmployeeAsync, getEmployeesAsync } from "../../../api/Employees";
import {
  CargandoTabla,
  EstadoError,
  EstadoVacio,
  PageHeader,
  useAviso,
} from "../../../components/base";
import { formatearFecha } from "../../../helpers/formato";
import {
  celdaFecha,
  encabezadoTabla,
  normalizar,
  useCargaLocal,
  useConfirmacion,
  useEscritura,
} from "./comunes";

const FILAS_POR_PAGINA = 10;
const ACTIVOS = 1;
const INACTIVOS = 2;

const EmployeeList = () => {
  const [employeeList, setEmployeeList] = useState([]);
  const [localReload, setLocalReload] = useState(false);

  const [tipoEmpleado, setTipoEmpleado] = useState(ACTIVOS);

  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir: pedirConfirmacion, dialogo: confirmacion } = useConfirmacion();

  let ruta = getRuta();
  const token = getToken();
  let navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [pagina, setPagina] = useState(0);

  // Menú de tres puntos de una fila.
  const [menu, setMenu] = useState({ ancla: null, fila: null });

  const termino = normalizar(searchTerm.trim());
  const withSearch = employeeList.filter((val) => {
    if (termino === "") return true;
    return (
      normalizar(val.id).includes(termino) ||
      normalizar(val.fullName).includes(termino) ||
      normalizar(val.identification).includes(termino)
    );
  });

  // Estado local de la lista: esqueleto mientras carga y error con "Reintentar".
  const { cargando, error, cargar } = useCargaLocal(() =>
    getEmployeesAsync(token, tipoEmpleado)
  );

  // Una sola carga por cambio de filtro o de recarga (como antes: GetEmployees/<tipo>).
  useEffect(() => {
    let vigente = true;
    (async () => {
      const result = await cargar();
      if (vigente && result) setEmployeeList(result.data.result);
    })();
    return () => {
      vigente = false;
    };
  }, [localReload, tipoEmpleado]);

  // Si al buscar, cambiar de filtro o desactivar la página actual queda fuera de rango, se vuelve
  // a la primera: así no aparece un "No hay datos" falso.
  useEffect(() => {
    if (pagina > 0 && pagina * FILAS_POR_PAGINA >= withSearch.length) {
      setPagina(0);
    }
  }, [pagina, withSearch.length]);

  const currentItem = withSearch.slice(
    pagina * FILAS_POR_PAGINA,
    (pagina + 1) * FILAS_POR_PAGINA
  );

  const onChangeSearch = (val) => {
    setPagina(0);
    setSearchTerm(val);
  };

  // Desactiva al empleado SIN calcular su liquidación (así funcionaba "Eliminar").
  const deleteEmployee = async (item) => {
    const ok = await escribir(
      () => deleteEmployeeAsync(token, item.id),
      "No se pudo desactivar al empleado"
    );
    if (!ok) return;
    setLocalReload(!localReload);
    aviso.exito(`Empleado ${item.fullName} desactivado`);
  };

  const onChangeEmpleado = (value) => {
    if (value === tipoEmpleado) return;
    // B14: al cambiar de filtro se vuelve a la primera página.
    setPagina(0);
    setTipoEmpleado(value);
  };

  const pedirDesactivar = (fila) =>
    pedirConfirmacion({
      titulo: "Desactivar empleado",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se desactivará a ${fila.fullName}. No se calcula su liquidación. También se quita de la nómina abierta: no se le paga el período en curso.`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Si debe recibir su liquidación, ábrelo y usa «Liquidar» en su ficha.
          </Typography>
        </>
      ),
      textoConfirmar: "Desactivar empleado",
      peligro: true,
      onConfirmar: () => deleteEmployee(fila),
    });

  const puedeAgregar = tienePermiso(access, "NOMINA CREAR");
  // Cada acción pide el permiso que exige su endpoint (EmployeesController): ver el empleado es
  // GetEmployeeById (NOMINA VER); desactivar es DeleteEmployee (NOMINA DELETE).
  const puedeVer = tienePermiso(access, "NOMINA VER");
  const puedeDesactivar =
    tienePermiso(access, "NOMINA DELETE") && tipoEmpleado === ACTIVOS;
  const mostrarAcciones =
    puedeVer || tienePermiso(access, "NOMINA DELETE");

  const hayBusqueda = termino !== "";
  const enActivos = tipoEmpleado === ACTIVOS;

  const botonAgregar = (
    <Button
      onClick={() => {
        navigate(`${ruta}/add-employees`);
      }}
      startIcon={<AddIcon />}
      variant="contained"
    >
      Agregar empleado
    </Button>
  );

  let contenido;
  if (cargando) {
    contenido = (
      <CargandoTabla
        filas={6}
        columnas={5}
        etiqueta={enActivos ? "Cargando empleados activos…" : "Cargando empleados inactivos…"}
      />
    );
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar los empleados"
        }
        onReintentar={() => setLocalReload(!localReload)}
      />
    );
  } else if (withSearch.length === 0) {
    contenido = hayBusqueda ? (
      <EstadoVacio
        titulo="Sin resultados"
        descripcion={`Ningún empleado coincide con «${searchTerm.trim()}». Revisa el número, el nombre o la cédula.`}
        accion={
          <Button variant="outlined" onClick={() => onChangeSearch("")}>
            Borrar búsqueda
          </Button>
        }
      />
    ) : enActivos ? (
      <EstadoVacio
        titulo="Todavía no hay empleados activos"
        descripcion="Aquí aparece el personal que está en la nómina. Agrega al primero para poder pagarle."
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    ) : (
      <EstadoVacio
        titulo="No hay empleados inactivos"
        descripcion="Aquí aparecen los empleados que ya se liquidaron o se desactivaron."
      />
    );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table aria-label={enActivos ? "Empleados activos" : "Empleados inactivos"}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}
                >
                  #
                </TableCell>
                <TableCell sx={encabezadoTabla}>Empleado</TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", md: "table-cell" } }}
                >
                  Cédula
                </TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", md: "table-cell" } }}
                >
                  Fecha de ingreso
                </TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", lg: "table-cell" } }}
                >
                  N.º INSS
                </TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}
                >
                  Puesto
                </TableCell>
                {mostrarAcciones ? (
                  <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                    Acciones
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentItem.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      fontVariantNumeric: "tabular-nums",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    {row.id}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.fullName}
                    </Typography>
                    {/* En pantallas pequeñas la cédula, el puesto y el ingreso pasan debajo del nombre. */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: { xs: "block", md: "none" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ display: { xs: "inline", sm: "none" } }}
                      >
                        {`#${row.id} · `}
                      </Typography>
                      {row.identification ?? "—"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: { xs: "block", sm: "none" },
                      }}
                    >
                      {row.puesto || "—"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: { xs: "block", md: "none" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {`Ingreso ${formatearFecha(row.fechaInicioContrato)}`}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {row.identification}
                  </TableCell>
                  <TableCell sx={{ ...celdaFecha, display: { xs: "none", md: "table-cell" } }}>
                    {formatearFecha(row.fechaInicioContrato)}
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                      display: { xs: "none", lg: "table-cell" },
                    }}
                  >
                    {row.inssNumber}
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    {row.puesto}
                  </TableCell>
                  {mostrarAcciones ? (
                    <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {puedeVer ? (
                        <Tooltip title="Ver empleado">
                          <IconButton
                            aria-label={`Ver al empleado ${row.fullName}`}
                            onClick={() => navigate(`${ruta}/employee/${row.id}`)}
                            sx={{ color: "primary.main" }}
                          >
                            <VisibilityOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {puedeDesactivar ? (
                        <Tooltip title="Más acciones">
                          <IconButton
                            aria-label={`Más acciones de ${row.fullName}`}
                            aria-haspopup="menu"
                            onClick={(e) => setMenu({ ancla: e.currentTarget, fila: row })}
                            sx={{ color: "text.secondary" }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={withSearch.length}
          page={pagina}
          onPageChange={(e, nueva) => setPagina(nueva)}
          rowsPerPage={FILAS_POR_PAGINA}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          getItemAriaLabel={(tipo) =>
            ({
              first: "Primera página",
              last: "Última página",
              next: "Página siguiente",
              previous: "Página anterior",
            }[tipo])
          }
        />
      </>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Empleados"
        subtitulo="Busca, abre y administra al personal de la nómina"
        icono={<FontAwesomeIcon icon={faUserTie} />}
        acciones={puedeAgregar ? botonAgregar : undefined}
      />

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Tabs
          value={enActivos ? 0 : 1}
          onChange={(_e, indice) => onChangeEmpleado(indice === 0 ? ACTIVOS : INACTIVOS)}
          aria-label="Estado del empleado"
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab
            label="Activos"
            id="empleados-tab-0"
            aria-controls="empleados-lista"
          />
          <Tab
            label="Inactivos"
            id="empleados-tab-1"
            aria-controls="empleados-lista"
          />
        </Tabs>

        <TextField
          size="small"
          type="search"
          onChange={(e) => onChangeSearch(e.target.value)}
          value={searchTerm}
          placeholder="Número, nombre o cédula"
          inputProps={{ "aria-label": "Buscar empleado", autoComplete: "off" }}
          sx={{ width: { xs: "100%", md: 380 }, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Borrar búsqueda"
                  onClick={() => onChangeSearch("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <div
          role="tabpanel"
          id="empleados-lista"
          aria-labelledby={enActivos ? "empleados-tab-0" : "empleados-tab-1"}
        >
          {contenido}
        </div>
      </Paper>

      <Menu
        anchorEl={menu.ancla}
        open={Boolean(menu.ancla)}
        onClose={() => setMenu((actual) => ({ ...actual, ancla: null }))}
      >
        <MenuItem
          onClick={() => {
            const fila = menu.fila;
            setMenu((actual) => ({ ...actual, ancla: null }));
            pedirDesactivar(fila);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <PersonOffOutlinedIcon />
          </ListItemIcon>
          <ListItemText>Desactivar</ListItemText>
        </MenuItem>
      </Menu>

      {confirmacion}
    </Container>
  );
};

export default EmployeeList;
